import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { House, SquaresFour, Sparkle, User, MagnifyingGlass, Basket, ChartBar, CheckCircle, Flame, Bell } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";
import { DemoNav, DemoFooter } from "@/components/DemoShell";
import { DemoChat } from "@/components/DemoChat";

const FITNESS_CHAT = {
    greeting: "Hey — I'm your HIGHTS Fitness assistant. Ask about plans, classes or your training.",
    suggestions: [
        { q: "Which plan suits me?", a: "Tell me your goal — strength, conditioning or all-round fitness — and how many sessions a week you can commit to, and I'll recommend a plan." },
        { q: "Can I book a class?", a: "Of course. Tomorrow there's Conditioning at 07:00 (4 spots) and Strength at 18:30 (2 spots). Want me to hold one?" },
        { q: "How do payments work?", a: "Plans bill monthly and you can pause anytime from your account. Everything syncs with the studio's CRM so the team always knows where you are." },
    ],
    handoff: "You're all set — in the live app this would sync straight to the studio's CRM with automated follow-up.",
};

const MARKET_CHAT = {
    greeting: "Welcome to HIGHTS Market. I can find products, compare sellers and track orders.",
    suggestions: [
        { q: "Find me a coffee machine under £300", a: "Three options from verified sellers: the Brewhouse Classic (£189, 4.8★), Barista Pro Mini (£249, 4.6★) and Moccia One (£299, 4.9★). Want a side-by-side comparison?" },
        { q: "Which seller is most reliable?", a: "I rank sellers on dispatch speed, returns and review velocity — not just stars. For appliances right now, Northgate Trading leads with 99.1% on-time dispatch." },
        { q: "Where is my order?", a: "Your last order is out for delivery, arriving today before 13:00. I've enabled live tracking notifications for you." },
    ],
    handoff: "Anything else? Sellers on HIGHTS Market get the same AI answering for their own customers, connected to their CRM.",
};

const inputCls = "h-11 w-full border border-white/15 bg-black px-4 text-sm text-white placeholder:text-white/30 focus:border-white/45 focus:outline-none";

function FitnessHome({ booked, onBook }) {
    const classes = [
        { time: "07:00", name: "Conditioning", spots: 4 },
        { time: "12:15", name: "Strength Foundations", spots: 6 },
        { time: "18:30", name: "Strength", spots: 2 },
    ];
    return (
        <div className="space-y-4 p-5">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs text-white/40">Good morning</p>
                    <p className="font-display text-xl font-bold text-white">Alex</p>
                </div>
                <span className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-xs text-white/70"><Flame size={13} /> 12-day streak</span>
            </div>
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Today's classes</p>
                <div className="mt-3 space-y-2">
                    {classes.map((c) => (
                        <div key={c.time} className="flex items-center justify-between border border-white/10 p-3">
                            <div>
                                <p className="text-sm font-bold text-white">{c.name}</p>
                                <p className="text-xs text-white/45">{c.time} · {c.spots} spots left</p>
                            </div>
                            <button
                                onClick={() => onBook(c)}
                                data-testid={`fitness-book-${c.time.replace(":", "")}`}
                                disabled={booked === c.time}
                                className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${booked === c.time ? "bg-white/10 text-white/50" : "bg-white text-black hover:bg-white/85"}`}
                            >
                                {booked === c.time ? "Booked" : "Book"}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
                {[["18", "visits"], ["4.2k", "kg lifted"], ["6", "PBs"]].map(([v, l]) => (
                    <div key={l} className="border border-white/12 p-3">
                        <p className="font-display text-lg font-black text-white">{v}</p>
                        <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/40">{l} / month</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FitnessPlans() {
    const [joined, setJoined] = useState(null);
    const plans = [
        { name: "Off-Peak", price: "£39/mo", desc: "Weekday daytime access" },
        { name: "Unlimited", price: "£59/mo", desc: "All classes, all hours" },
        { name: "Performance", price: "£99/mo", desc: "Unlimited + monthly coaching" },
    ];
    return (
        <div className="space-y-3 p-5">
            {plans.map((p) => (
                <div key={p.name} className="border border-white/12 bg-elev p-4" data-testid={`fitness-plan-${p.name.toLowerCase()}`}>
                    <div className="flex items-baseline justify-between">
                        <p className="font-display text-base font-bold text-white">{p.name}</p>
                        <p className="font-mono text-sm text-white/70">{p.price}</p>
                    </div>
                    <p className="mt-1 text-xs font-light text-white/45">{p.desc}</p>
                    <button
                        onClick={() => { setJoined(p.name); toast.success(`Joined ${p.name} — simulated checkout, no payment taken.`); }}
                        data-testid={`fitness-join-${p.name.toLowerCase()}`}
                        className={`mt-3 w-full py-2.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${joined === p.name ? "bg-white/10 text-white/60" : "bg-white text-black hover:bg-white/85"}`}
                    >
                        {joined === p.name ? "Active" : "Join — simulated checkout"}
                    </button>
                </div>
            ))}
            <p className="text-center text-[10px] text-white/30">Payments in this prototype are simulated — no charge is ever made.</p>
        </div>
    );
}

function FitnessYou() {
    return (
        <div className="space-y-4 p-5">
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Next session</p>
                <p className="mt-2 font-display text-lg font-bold text-white">Strength — tomorrow 18:30</p>
                <p className="text-xs text-white/45">Reminder set · synced to studio CRM</p>
            </div>
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Notifications</p>
                <div className="mt-3 space-y-2.5">
                    {["Class confirmed — booking #2481", "Payment receipt — £59.00", "Coach Mia left feedback on your session"].map((n) => (
                        <p key={n} className="flex items-start gap-2 text-xs font-light text-white/60"><Bell size={13} className="mt-0.5 shrink-0 text-white/40" />{n}</p>
                    ))}
                </div>
            </div>
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Progress</p>
                <div className="mt-3 flex h-20 items-end gap-1.5">
                    {[35, 50, 42, 60, 55, 72, 68, 84].map((h, i) => (
                        <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: i * 0.06 }} className="flex-1 bg-white/70" />
                    ))}
                </div>
                <p className="mt-2 text-[10px] text-white/35">Training volume — last 8 weeks</p>
            </div>
        </div>
    );
}

function MarketHome({ basket, onAdd }) {
    const [q, setQ] = useState("");
    const products = [
        { id: "brew", name: "Brewhouse Classic", vendor: "Northgate Trading", price: 189, tag: "Appliances" },
        { id: "lamp", name: "Studio Desk Lamp", vendor: "Ferris & Co", price: 79, tag: "Home" },
        { id: "chair", name: "Ergo Task Chair", vendor: "Workspace Direct", price: 249, tag: "Office" },
        { id: "press", name: "Moccia One", vendor: "Northgate Trading", price: 299, tag: "Appliances" },
    ];
    const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || p.tag.toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="space-y-4 p-5">
            <div className="relative">
                <MagnifyingGlass size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="market-search" placeholder="Search thousands of local sellers…" className={`${inputCls} pl-10`} />
            </div>
            <div className="flex gap-1.5">
                {["Appliances", "Home", "Office"].map((c) => (
                    <button key={c} onClick={() => setQ(c)} className="border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-widest text-white/55 transition-colors hover:border-white/45 hover:text-white" data-testid={`market-cat-${c.toLowerCase()}`}>{c}</button>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
                {filtered.map((p) => (
                    <div key={p.id} className="border border-white/12 bg-elev p-3" data-testid={`market-product-${p.id}`}>
                        <div className="flex h-16 items-center justify-center border border-white/10 bg-black font-display text-xl font-black text-white/20">{p.name[0]}</div>
                        <p className="mt-2 text-xs font-bold text-white">{p.name}</p>
                        <p className="text-[10px] text-white/40">{p.vendor}</p>
                        <div className="mt-2 flex items-center justify-between">
                            <span className="font-mono text-xs text-white/80">£{p.price}</span>
                            <button onClick={() => { onAdd(p); toast.success(`${p.name} added to basket.`); }} data-testid={`market-add-${p.id}`}
                                className="bg-white px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-black hover:bg-white/85">Add</button>
                        </div>
                    </div>
                ))}
            </div>
            {basket.length > 0 && <p className="text-center text-[10px] text-white/40">{basket.length} item(s) in basket</p>}
        </div>
    );
}

function MarketBasket({ basket }) {
    const [done, setDone] = useState(false);
    const total = basket.reduce((s, p) => s + p.price, 0);
    return (
        <div className="space-y-3 p-5">
            {basket.length === 0 && !done && <p className="py-10 text-center text-sm text-white/40">Your basket is empty — add something from Discover.</p>}
            {basket.map((p, i) => (
                <div key={i} className="flex items-center justify-between border border-white/12 bg-elev p-3.5">
                    <div>
                        <p className="text-xs font-bold text-white">{p.name}</p>
                        <p className="text-[10px] text-white/40">{p.vendor}</p>
                    </div>
                    <span className="font-mono text-xs text-white/80">£{p.price}</span>
                </div>
            ))}
            {basket.length > 0 && !done && (
                <button onClick={() => { setDone(true); toast.success("Order placed — simulated checkout."); }} data-testid="market-checkout"
                    className="w-full bg-white py-3.5 text-[11px] font-bold uppercase tracking-[0.22em] text-black hover:bg-white/85">
                    Checkout £{total} — simulated
                </button>
            )}
            {done && (
                <div className="border border-white/15 bg-elev p-5 text-center" data-testid="market-order-success">
                    <CheckCircle size={26} weight="fill" className="mx-auto text-white" />
                    <p className="mt-3 text-sm font-bold text-white">Order confirmed</p>
                    <p className="mt-1 text-xs font-light text-white/50">Delivery updates automated. Seller CRMs notified. Payment simulated.</p>
                </div>
            )}
        </div>
    );
}

function MarketBusiness() {
    return (
        <div className="space-y-4 p-5">
            <div className="grid grid-cols-3 gap-2 text-center">
                {[["£8.4k", "revenue"], ["312", "orders"], ["4.9★", "rating"]].map(([v, l]) => (
                    <div key={l} className="border border-white/12 p-3">
                        <p className="font-display text-base font-black text-white">{v}</p>
                        <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/40">{l} / 30d</p>
                    </div>
                ))}
            </div>
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Sales — 8 weeks</p>
                <div className="mt-3 flex h-20 items-end gap-1.5">
                    {[30, 45, 38, 55, 62, 58, 75, 90].map((h, i) => (
                        <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: i * 0.06 }} className="flex-1 bg-white/70" />
                    ))}
                </div>
            </div>
            <div className="border border-white/12 bg-elev p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">CRM feed</p>
                <div className="mt-3 space-y-2.5">
                    {["New wholesale enquiry — qualified 88", "Repeat customer flagged for loyalty offer", "Review request sent — order #1182"].map((n) => (
                        <p key={n} className="flex items-start gap-2 text-xs font-light text-white/60"><Bell size={13} className="mt-0.5 shrink-0 text-white/40" />{n}</p>
                    ))}
                </div>
            </div>
        </div>
    );
}

const APPS = {
    fitness: {
        name: "HIGHTS FITNESS", sector: "Mobile app demo", chat: FITNESS_CHAT,
        tabs: [
            { id: "home", label: "Home", icon: House },
            { id: "plans", label: "Plans", icon: SquaresFour },
            { id: "assistant", label: "AI Coach", icon: Sparkle },
            { id: "you", label: "You", icon: User },
        ],
    },
    market: {
        name: "HIGHTS MARKET", sector: "Marketplace app demo", chat: MARKET_CHAT,
        tabs: [
            { id: "home", label: "Discover", icon: House },
            { id: "basket", label: "Basket", icon: Basket },
            { id: "assistant", label: "Assistant", icon: Sparkle },
            { id: "business", label: "Business", icon: ChartBar },
        ],
    },
};

export default function AppDemo() {
    const { appId } = useParams();
    const app = APPS[appId];
    const [tab, setTab] = useState("home");
    const [booked, setBooked] = useState(null);
    const [basket, setBasket] = useState([]);

    if (!app) return <Navigate to="/" replace />;

    const onBook = (c) => { setBooked(c.time); toast.success(`${c.name} booked for ${c.time} — confirmation sent (simulated).`); };

    return (
        <div className="min-h-screen bg-base" data-testid={`app-demo-${appId}`}>
            <SEO title={`${app.name} — Interactive App Demo | HIGHTS`} description={`A working prototype of ${app.name}, built by HIGHTS to demonstrate bespoke application development connected to CRM and automation.`} path={`/demos/app/${appId}`} />
            <DemoNav name={app.name} sector={app.sector} />
            <div className="mx-auto max-w-[1400px] px-5 pb-24 pt-28 md:px-10">
                <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">Interactive prototype</p>
                        <h1 className="mt-5 font-display text-5xl font-black tracking-tighter text-white sm:text-6xl">{app.name}</h1>
                        <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60">
                            {appId === "fitness"
                                ? "Accounts, plans, payments, class booking, progress tracking and an AI coach — every tap connected to the studio's CRM."
                                : "A multi-business marketplace: discovery, comparison, an AI shopping assistant, checkout, delivery updates and a seller analytics dashboard."}
                        </p>
                        <ul className="mt-8 space-y-2.5 text-sm font-light text-white/55">
                            {(appId === "fitness"
                                ? ["Book a class and watch confirmations fire", "Join a plan through simulated checkout", "Ask the AI coach anything", "See the member dashboard sync to CRM"]
                                : ["Search and add products from independent sellers", "Run a simulated checkout", "Ask the AI shopping assistant", "View the seller business dashboard"]
                            ).map((s) => (
                                <li key={s} className="flex items-center gap-3"><span className="h-1 w-1 bg-white/50" />{s}</li>
                            ))}
                        </ul>
                        <p className="mt-8 max-w-md text-[11px] font-light leading-relaxed text-white/35">
                            This is a clickable prototype — accounts and payments are simulated. In production, every action connects to real systems.
                        </p>
                    </div>
                    <div className="flex justify-center">
                        <div className="w-full max-w-[380px] rounded-[2.6rem] border border-white/20 bg-black p-2.5 shadow-[0_40px_120px_rgba(0,0,0,0.8)]" data-testid="phone-frame">
                            <div className="overflow-hidden rounded-[2.1rem] border border-white/10 bg-base">
                                <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
                                    <span className="font-display text-xs font-black tracking-[0.2em] text-white">{app.name}</span>
                                    <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35">Prototype</span>
                                </div>
                                <div className="h-[480px] overflow-y-auto" data-lenis-prevent>
                                    {tab === "home" && (appId === "fitness" ? <FitnessHome booked={booked} onBook={onBook} /> : <MarketHome basket={basket} onAdd={(p) => setBasket((b) => [...b, p])} />)}
                                    {tab === "plans" && <FitnessPlans />}
                                    {tab === "basket" && <MarketBasket basket={basket} />}
                                    {tab === "assistant" && (
                                        <div className="flex h-full flex-col">
                                            <DemoChat title={appId === "fitness" ? "AI Coach" : "Shopping Assistant"} subtitle={`${app.name} · live AI`} greeting={app.chat.greeting} suggestions={app.chat.suggestions} handoff={app.chat.handoff} live agentId={appId} />
                                        </div>
                                    )}
                                    {tab === "you" && <FitnessYou />}
                                    {tab === "business" && <MarketBusiness />}
                                </div>
                                <div className="flex border-t border-white/10">
                                    {app.tabs.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => setTab(t.id)}
                                            data-testid={`app-tab-${t.id}`}
                                            className={`flex flex-1 flex-col items-center gap-1 py-3 transition-colors ${tab === t.id ? "text-white" : "text-white/35 hover:text-white/70"}`}
                                        >
                                            <t.icon size={18} weight={tab === t.id ? "fill" : "light"} />
                                            <span className="text-[8px] font-bold uppercase tracking-[0.15em]">{t.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DemoFooter name={app.name} />
        </div>
    );
}
